import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button,
  Tree,
  Input,
  Skeleton,
  Tooltip,
  notification,
  message,
  Popover,
  Space,
} from 'antd';
import PropTypes from 'prop-types';

import {
  FolderOrangeIcon,
  FolderDarkGreenIcon,
  FolderBackIcon,
  FolderBlueIcon,
  AddFolder,
  DeleteIcon,
  FolderArrow,
  ChevronRight,
  FolderIcon,
  EditIcon,
  SavedIcon,
  AddBlockIcon,
} from 'components/Icons';
import {
  CONTENT_LIBRARY_ADD_CHILD,
  CONTENT_LIBRARY_ADD_ITEM,
  CONTENT_LIBRARY_DELETE_CHILD,
  CONTENT_LIBRARY_RENAME_CHILD,
} from 'graphql/mutations/contentLibraryMutations';
import { useContentLibrary } from 'context/ContentLibraryContext';
import Prompt from 'components/Prompt';

import './ContentLibraryTree.scss';

const TreeNode = ({
  data,
  onDelete,
  onUpdateName,
  onDrop,
  activeTreeNode,
  treeUid,
  userId,
  userRole,
  disableEditing,
}) => {
  const [editable, makeEditable] = useState(false);
  const [value, updateValue] = useState(data.title);
  const [visible, toggleEdit] = useState(false);

  useEffect(() => {
    if (!activeTreeNode) {
      makeEditable(false);
    }
  }, [activeTreeNode]);

  // Render divider if this is a divider item
  if (data.isDivider) {
    return <div className="tree-divider"></div>;
  }

  const canUserUpdate = treeUid === userId || data.uid === userId || userRole === 'manager';
  return (
    <span
      className="content-library-tree-title"
      onDrop={onDrop}
      onDragOver={(ev) => ev.preventDefault()}>
      {!data.default && editable && canUserUpdate ? (
        <input
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.keyCode === 13 && value !== '') {
              e.preventDefault();
              if (onUpdateName) {
                onUpdateName(value);
                makeEditable(false);
              }
            }
          }}
          onBlur={(e) => {
            if (value !== '') {
              if (onUpdateName) {
                onUpdateName(value);
                makeEditable(false);
              }
            }
          }}
          onInput={(e) => updateValue(e.target.value)}
          value={value}
          maxLength={50}
        />
      ) : (
        <Tooltip title={value?.length > 30 ? value : ''}>
          <span onDoubleClick={() => !data.default && canUserUpdate && makeEditable(true)}>
            {value}
          </span>
        </Tooltip>
      )}

      {!data.default && canUserUpdate && !disableEditing && (
        <>
          {editable ? (
            <SavedIcon
              className="edit-icon"
              size="12"
              onClick={(e) => {
                e.stopPropagation();
                if (value !== '') {
                  onUpdateName(value);
                  makeEditable(false);
                }
              }}
              disabled={value === ''}
            />
          ) : (
            <EditIcon
              className="edit-icon"
              size="12"
              onClick={(e) => {
                e.stopPropagation();
                makeEditable(true);
              }}
            />
          )}

          <Popover
            onClick={(e) => e.stopPropagation()}
            trigger="click"
            visible={visible}
            onVisibleChange={() => toggleEdit(!visible)}
            content={
              <div className="delete-confirm" onClick={(e) => e.stopPropagation()}>
                <h4>Are you sure to delete this folder?</h4>
                <p>Deleting this folder will also delete sub folders & its contents.</p>
                <div className="buttons">
                  <Space size="large">
                    <Button onClick={onDelete} className="delete-btn-small">
                      DELETE
                    </Button>
                    <Button onClick={() => toggleEdit(false)}>CANCEL</Button>
                  </Space>
                </div>
              </div>
            }>
            <DeleteIcon
              size="18"
              className={`delete-icon ${visible ? 'delete-icon-visible' : ''}`}
            />
          </Popover>
        </>
      )}
      {disableEditing && !data.create && !data.isSearchResultItem && (
        <div className="disable-editing" style={{ pointerEvents: 'none' }}>
          <ChevronRight />
        </div>
      )}
    </span>
  );
};

/**
 * Content library tree
 *
 * @param {Object} props
 * @param {boolean} props.fromModal
 * @param {Function} props.onSelectTreeItem
 * @param {string} props.type
 * @param {Function} props.onUpdate
 * @param {string} props.userId
 * @param {string} props.userRole
 * @param {boolean} props.isLocked
 * @param {boolean} props.searchActive
 * @param {string} props.toFolderKey
 * @param {Function} props.setFolderName
 * @param {boolean} props.disableNewFolder
 * @param {boolean} props.disableEditing
 * @param {boolean} props.isDefaultFolder
 * @param {boolean} props.isWixServices
 * @param {boolean} props.isWixProducts
 * @param {string} props.selectedPrelistFolderKey
 */
const ContentLibraryTree = forwardRef(
  (
    {
      fromModal,
      onSelectTreeItem,
      onUpdate,
      userId,
      userRole,
      isLocked,
      searchActive,
      toFolderKey,
      disableNewFolder,
      disableEditing,
      isDefaultFolder,
      isWixServices,
      isWixProducts,
      treeData,
      onKeySelect,
      type,
      selectedPrelistFolderKey,
      expandAll,
      selectedDeliverablesFolderKeys,
      hasSearchResults,
      onSearchResultItemClick, // New prop for handling search result item clicks
    },
    ref
  ) => {
    // Get treeData and related functions from context
    const {
      loadingTreeView,
      handleRefetch,
      handleTreeData,
      setIndexes,
      initialTreeData,
      handleModalTreeSearch,
      handleTreeSearch,
      addKey,
      selectedFolderKey,
      selectedFolderLevel,
      selectedFolderName,
      getDefaultName,
    } = useContentLibrary();

    const [newFolderPopup, toggleNewFolderPopup] = useState(false);
    const [treeSearch, setTreeSearch] = useState('');
    const [searchTreeData, setSearchTreeData] = useState(treeData);
    const [expandedKeysState, setExpandedKeysState] = useState(null);

    // Update search tree data when main tree data changes
    useEffect(() => {
      setSearchTreeData(treeData);
    }, [treeData]);

    /**
     * Use imperative handle
     */
    useImperativeHandle(ref, () => ({
      async reloadItems() {
        await handleRefetch('', onKeySelect);
      },
      getTreeData() {
        return treeData;
      },
    }));

    /**
     * Add child mutation
     */
    const [addChild] = useMutation(CONTENT_LIBRARY_ADD_CHILD, {
      onCompleted() {
        onSelectTreeItem({
          key: 'newTemplateFolder',
          value: false,
        });
        notification.success({
          description: 'Created new folder successfully.',
        });
        handleRefetch(treeSearch, onKeySelect);
        setTreeSearch('');
      },
    });

    /**
     * Add item mutation
     */
    const [addItem] = useMutation(CONTENT_LIBRARY_ADD_ITEM, {
      onCompleted() {
        notification.success({
          description: 'Item moved successfully.',
        });
        if (onUpdate) {
          onUpdate();
        }
        handleRefetch('', onKeySelect);
      },
    });

    /**
     * Delete child mutation
     */
    const [deleteChild] = useMutation(CONTENT_LIBRARY_DELETE_CHILD, {
      onCompleted() {
        notification.success({
          description: 'Folder deleted successfully.',
        });
        handleRefetch('', onKeySelect);
        onKeySelect([[type]]);
      },
    });

    /**
     * Rename child mutation
     */
    const [renameChild] = useMutation(CONTENT_LIBRARY_RENAME_CHILD, {
      onCompleted() {
        notification.success({
          description: 'Folder renamed successfully.',
        });
        handleRefetch('', onKeySelect);
      },
    });

    /**
     * Add new child from search
     *
     * @param {Array} k
     */
    const addNewChildFromSearch = (k) => {
      let _selectedFolderKey = selectedFolderKey || '';

      if (
        _selectedFolderKey === 'newTemplateFolder' ||
        _selectedFolderKey === getDefaultName(type, 'key') ||
        _selectedFolderKey === 'starred-folder' ||
        _selectedFolderKey === 'shared-folder' ||
        _selectedFolderKey === 'default-deliverables' ||
        _selectedFolderKey === 'wix-services' ||
        _selectedFolderKey === 'wix-products' ||
        _selectedFolderKey === 'pipedrive-products' ||
        _selectedFolderKey === 'hubspot-products' ||
        _selectedFolderKey === 'deliverables'
      ) {
        _selectedFolderKey = '';
      }

      onSelectTreeItem({
        key: 'newTemplateFolder',
        value: true,
      });

      addChild({
        variables: {
          child: { title: treeSearch?.trim(), uid: userId },
          key: `${_selectedFolderKey}${_selectedFolderKey ? '.' : ''}children`,
          id: treeData._id, // Always use the main tree data ID for mutations
        },
      });
    };

    /**
     * Handle search key down
     *
     * @param {Object} e
     */
    const handleSearchKeyDown = (e) => {
      if (e.key === 'Enter') {
        const currentTreeData = fromModal && treeSearch ? searchTreeData : treeData;
        if (currentTreeData?.children?.length === 1) {
          const k = currentTreeData.children?.[0]?.key?.split('_');
          if (k?.[0] === 'newTemplateFolder' || k?.[k?.length - 1] === 'new') {
            addNewChildFromSearch([k]);
          }
        }
      }
    };

    const selectedKeys = [
      [selectedFolderKey, selectedFolderLevel, selectedFolderName || selectedPrelistFolderKey]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join('_'),
    ];

    // Combine selectedKeys with selectedDeliverablesFolderKeys for expanded keys
    const baseExpandedKeys = [...selectedKeys, ...(selectedDeliverablesFolderKeys || [])].filter(
      Boolean
    );

    // When selection changes, merge to ensure selected path stays expanded
    useEffect(() => {
      if (expandedKeysState !== null && baseExpandedKeys.length > 0) {
        setExpandedKeysState((prev) => {
          const merged = new Set([...baseExpandedKeys, ...(prev || [])]);
          return Array.from(merged);
        });
      }
      // Only run when selection changes - expandedKeysState intentionally excluded to avoid loop
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFolderKey, selectedDeliverablesFolderKeys]);

    const expandedKeys = expandedKeysState !== null ? expandedKeysState : baseExpandedKeys;

    /**
     * Render
     */
    if (!treeData) return null;
    return (
      <div className="split-d1">
        {searchActive && (
          <Input
            className="tree-node-search-input"
            value={treeSearch}
            style={{ marginBottom: 8 }}
            placeholder={`Search or create ${
              selectedFolderKey?.startsWith('children.') ? 'sub' : ''
            }folder (↵ to create)`}
            suffix={<AddBlockIcon onClick={() => handleSearchKeyDown({ key: 'Enter' })} />}
            onChange={(e) => {
              const searchValue = e.target.value;
              setTreeSearch(searchValue);

              if (fromModal) {
                // Use modal tree search to avoid affecting main tree data
                const modifiedTreeData = handleModalTreeSearch({
                  tempSearchValue: searchValue,
                  treeData,
                  setIndexes,
                  initialTreeData,
                });
                modifiedTreeData.key = '';
                addKey(modifiedTreeData);
                setSearchTreeData(modifiedTreeData);
              } else {
                // Use main tree search for non-modal contexts
                handleTreeSearch({
                  tempSearchValue: searchValue,
                  treeData,
                  setIndexes,
                  handleTreeData,
                  initialTreeData,
                  keyToBeSelected: '',
                  fromModal: false,
                  type: '',
                  isDefaultFolder,
                  isWixServices,
                  isWixProducts,
                  onKeySelect,
                  toFolderKey,
                });
              }
            }}
            onKeyDown={handleSearchKeyDown}
          />
        )}
        <div className="sidemenu-list">
          {loadingTreeView ? (
            Array.from({ length: 3 }, (_, arrayIndex) => (
              <Skeleton.Input key={arrayIndex} active={true} />
            ))
          ) : (
            <Tree
              allowDrop={() => {}}
              showLine
              showIcon
              blockNode
              selectable
              defaultExpandAll={expandAll}
              autoExpandParent={true}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeysState(keys)}
              selectedKeys={selectedKeys}
              onSelect={(keys, info) => {
                setExpandedKeysState(keys);
                const k = keys?.map((k) => k?.split('_'));
                const selectedNode = info.node;

                // Don't allow selection of divider items
                if (k?.[0]?.[0] === 'divider-integration') {
                  return;
                }

                // Handle search result item clicks - check both isSearchResultItem and key pattern
                const hasSearchInKey = k?.[0]?.some((part) => part === 'search');
                const isSearchResult =
                  selectedNode.isSearchResultItem || (hasSearchInKey && selectedNode.searchItem);
                if (isSearchResult && onSearchResultItemClick) {
                  // Ensure we have the searchItem data
                  if (selectedNode.searchItem) {
                    onSearchResultItemClick(selectedNode.searchItem, selectedNode.itemId);
                    return;
                  }
                }

                if (hasSearchInKey && !selectedNode.searchItem && onSearchResultItemClick) {
                  const findSearchItemInTree = (nodes) => {
                    if (!nodes || !Array.isArray(nodes)) return null;
                    for (const node of nodes) {
                      if (node.key === keys[0] && node.searchItem) {
                        return node.searchItem;
                      }
                      if (node.children) {
                        const found = findSearchItemInTree(node.children);
                        if (found) return found;
                      }
                    }
                    return null;
                  };
                  const searchItem = findSearchItemInTree(treeData?.children);
                  if (searchItem) {
                    onSearchResultItemClick(searchItem, selectedNode.itemId);
                    return;
                  }
                }

                // Don't allow selection of search result items (fallback)
                if (hasSearchInKey) {
                  return;
                }

                // Disable folder selection when search results are active
                if (hasSearchResults) {
                  return;
                }

                if (k?.[0]?.length >= 4 && k?.[0]?.[k[0].length - 1] === 'new') {
                  addNewChildFromSearch(k);
                } else {
                  onKeySelect(k);
                  onSelectTreeItem({
                    key: k?.map(([v]) => v)?.[0],
                    treeId: treeData._id,
                    name: k?.[0]?.[2] || '',
                  });
                }
              }}
              treeData={(
                (fromModal && treeSearch ? searchTreeData : treeData).children || []
              ).filter((item) => item.title !== null || item.isDivider)}
              icon={(data) => {
                // For search result items, show the item image instead of folder icon
                if (data?.isSearchResultItem) {
                  if (data?.image) {
                    return (
                      <img
                        src={data.image}
                        alt={data.title}
                        style={{
                          width: '20px',
                          height: '20px',
                          objectFit: 'cover',
                          borderRadius: '50%',
                          marginRight: '8px',
                        }}
                        onError={(e) => {
                          // Fallback to folder icon if image fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  }
                  return null;
                }
                // Regular folder icons
                return data?.isDivider ? null : data?.create ? (
                  <AddFolder />
                ) : data?.title?.includes('Default') ? (
                  <FolderBlueIcon />
                ) : data?.title?.includes('HubSpot Products') ? (
                  <FolderOrangeIcon />
                ) : data?.title?.includes('Pipedrive Products') ? (
                  <FolderDarkGreenIcon />
                ) : data?.title?.includes('Wix Products') ||
                  data?.title?.includes('Wix Services') ? (
                  <FolderBackIcon />
                ) : data?.title ? (
                  <FolderIcon />
                ) : (
                  <></>
                );
              }}
              switcherIcon={!hasSearchResults ? <FolderArrow /> : <span />}
              titleRender={(data) => (
                <TreeNode
                  onDrop={(ev) => {
                    ev.preventDefault();
                    // Don't allow drop on divider items
                    if (
                      data.isDivider ||
                      selectedFolderKey === data.path ||
                      data.key === 'newTemplateFolder'
                    )
                      return;
                    const body = JSON.parse(ev.dataTransfer.getData('data'));
                    addItem({
                      variables: {
                        fromKey: selectedFolderKey || '',
                        id: treeData._id, // Always use the main tree data ID for mutations
                        key: data.path,
                        itemId: body._id,
                        type,
                      },
                    });
                  }}
                  activeTreeNode={
                    selectedFolderKey === data.path || data.key === 'new_template_folder'
                  }
                  checkedKeys={[selectedFolderKey]}
                  data={data}
                  userId={userId}
                  userRole={userRole}
                  treeUid={treeData.uid}
                  disableEditing={disableEditing}
                  onDelete={() => {
                    // eslint-disable-next-line
                    deleteChild({
                      variables: {
                        id: treeData._id, // Always use the main tree data ID for mutations
                        key: data.path,
                      },
                    });
                  }}
                  onUpdateName={(name) => {
                    renameChild({
                      variables: {
                        id: treeData._id, // Always use the main tree data ID for mutations
                        key: data.path,
                        name,
                      },
                    });
                  }}
                />
              )}
            />
          )}
        </div>
        {!disableNewFolder && (
          <div className="footer-button">
            <Button
              disabled={selectedFolderLevel > 3 || isLocked}
              className="large-btn grey-btn"
              icon={<AddFolder />}
              block
              onClick={() => toggleNewFolderPopup(true)}>
              New Folder
            </Button>
          </div>
        )}
        {newFolderPopup && (
          <Prompt
            modalProps={{ centered: true }}
            visible={newFolderPopup}
            cancelText="CANCEL"
            confirmText="ADD"
            className="new-folder-prompt"
            handleCancel={() => toggleNewFolderPopup(false)}
            maxLength={40}
            handleConfirm={(title) => {
              toggleNewFolderPopup(false);
              const trimmedTitle = title?.trim();
              if (trimmedTitle) {
                let ifExists = false;
                for (let child of treeData.children) {
                  if (child.title === trimmedTitle) {
                    ifExists = true;
                  }
                }
                if (ifExists) {
                  return message.error('Folder already exists with same name');
                }

                let _selectedFolderKey = selectedFolderKey || '';
                if (
                  _selectedFolderKey === 'newTemplateFolder' ||
                  _selectedFolderKey === getDefaultName(type, 'key') ||
                  _selectedFolderKey === 'starred-folder' ||
                  _selectedFolderKey === 'shared-folder' ||
                  _selectedFolderKey === 'default-deliverables' ||
                  _selectedFolderKey === 'wix-services' ||
                  _selectedFolderKey === 'wix-products' ||
                  _selectedFolderKey === 'pipedrive-products' ||
                  _selectedFolderKey === 'hubspot-products' ||
                  _selectedFolderKey === 'deliverables'
                ) {
                  _selectedFolderKey = '';
                }

                addChild({
                  variables: {
                    child: { title: trimmedTitle, uid: userId },
                    key: `${_selectedFolderKey}${_selectedFolderKey ? '.' : ''}children`,
                    id: treeData._id, // Always use the main tree data ID for mutations
                  },
                });
              } else {
                message.error('Folder name required.');
              }
            }}
            title="Enter Folder Name"
          />
        )}
      </div>
    );
  }
);

/**
 * Default props
 */
ContentLibraryTree.defaultProps = {
  onSelectTreeItem: () => {},
  type: '',
  userId: '',
  userRole: '',
  isLocked: false,
  searchActive: false,
  disableNewFolder: false,
  disableEditing: false,
  isDefaultFolder: false,
  isWixServices: false,
  isWixProducts: false,
};

/**
 * Props
 */
ContentLibraryTree.propTypes = {
  onKeySelect: PropTypes.func,
  onUpdate: PropTypes.func.isRequired,
  onSelectTreeItem: PropTypes.func,
  type: PropTypes.string,
  userId: PropTypes.string,
  userRole: PropTypes.string,
  isLocked: PropTypes.bool,
  searchActive: PropTypes.bool,
  disableNewFolder: PropTypes.bool,
  disableEditing: PropTypes.bool,
  isDefaultFolder: PropTypes.bool,
  isWixServices: PropTypes.bool,
  isWixProducts: PropTypes.bool,
  selectedPrelistFolderKey: PropTypes.string,
};

export default ContentLibraryTree;
